import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Receipt, Search, Trash2 } from 'lucide-react';
import Toast from '../../components/Toast';
import { useToast } from '../../hooks/useToast';
import apiService from '../../services/api';
import {
  NR2J_INVOICE_LIST_FILTERS,
  formatCentsAsEuros,
  formatNr2jDateOnly,
  matchesInvoiceListFilter,
  nr2jInvoiceDisplayBadge,
  organizationDisplayName,
} from '../../utils/nr2jConstants';

export default function Nr2jInvoicesList() {
  const navigate = useNavigate();
  const { toasts, toast, removeToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [q, setQ] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.listNr2jInvoices({
        q: q.trim() || undefined,
      });
      setItems(res?.invoices || []);
    } catch (error) {
      toast.error(error?.message || 'Impossible de charger les factures');
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const filtered = useMemo(
    () => items.filter((inv) => matchesInvoiceListFilter(inv, statusFilter)),
    [items, statusFilter],
  );

  async function handleDelete(invoice) {
    if (invoice.status !== 'DRAFT') return;
    if (!window.confirm('Supprimer définitivement ce brouillon de facture ?')) return;
    setBusyId(invoice.id);
    try {
      await apiService.deleteNr2jInvoice(invoice.id);
      toast.success('Brouillon supprimé');
      load();
    } catch (error) {
      toast.error(error?.message || 'Suppression impossible');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="space-y-6" data-testid="nr2j-invoices-list">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Facturation</h1>
          <p className="text-sm text-gray-600">
            Factures, paiements et avoirs NR2J.
          </p>
        </div>
        <Link
          to="/nr2j/facturation/nouveau"
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white"
        >
          <Plus className="h-4 w-4" /> Nouvelle facture
        </Link>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-md flex-1">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher par numéro ou société…"
            className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm"
          />
        </div>
        <div className="flex flex-wrap gap-1">
          {NR2J_INVOICE_LIST_FILTERS.map((f) => (
            <button
              key={f.id || 'all'}
              type="button"
              onClick={() => setStatusFilter(f.id)}
              className={`rounded-full px-3 py-1 text-xs font-medium ${
                statusFilter === f.id
                  ? 'bg-indigo-600 text-white'
                  : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <p className="p-6 text-sm text-gray-500">Chargement…</p>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center">
            <Receipt className="mx-auto h-8 w-8 text-gray-300" />
            <p className="mt-3 text-sm text-gray-600">Aucune facture pour ce filtre.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3">Numéro</th>
                  <th className="px-4 py-3">Client</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Échéance</th>
                  <th className="px-4 py-3">TTC</th>
                  <th className="px-4 py-3">Payé</th>
                  <th className="px-4 py-3">Avoirs</th>
                  <th className="px-4 py-3">Solde</th>
                  <th className="px-4 py-3">État</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((invoice) => {
                  const badge = nr2jInvoiceDisplayBadge(invoice);
                  return (
                    <tr
                      key={invoice.id}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => navigate(`/nr2j/facturation/${invoice.id}`)}
                    >
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {invoice.number || 'Brouillon'}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {organizationDisplayName(invoice.organization)}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {formatNr2jDateOnly(invoice.issuedAt || invoice.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {formatNr2jDateOnly(invoice.dueAt)}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {formatCentsAsEuros(invoice.totalTtcCents)} €
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {formatCentsAsEuros(invoice.totalPaymentsCents)} €
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {formatCentsAsEuros(invoice.totalCreditsIssuedCents)} €
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {formatCentsAsEuros(invoice.balanceCents)} €
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs ${badge.className}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          <Link
                            to={`/nr2j/facturation/${invoice.id}`}
                            className="text-xs text-indigo-700 hover:underline"
                          >
                            Ouvrir
                          </Link>
                          {invoice.hasPdf || invoice.status === 'ISSUED' || invoice.status === 'SENT' || invoice.status === 'DRAFT' ? (
                            <button
                              type="button"
                              className="text-xs text-gray-600 hover:underline"
                              disabled={busyId === invoice.id}
                              onClick={async () => {
                                try {
                                  await apiService.downloadNr2jInvoicePdf(invoice.id, {
                                    number: invoice.number,
                                  });
                                } catch (error) {
                                  toast.error(error?.message || 'PDF indisponible');
                                }
                              }}
                            >
                              PDF
                            </button>
                          ) : null}
                          {invoice.status === 'DRAFT' ? (
                            <button
                              type="button"
                              disabled={busyId === invoice.id}
                              className="inline-flex items-center gap-0.5 text-xs text-rose-700 hover:underline"
                              onClick={() => handleDelete(invoice)}
                            >
                              <Trash2 className="h-3 w-3" /> Supprimer
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {toasts.map((item) => (
        <Toast key={item.id} toast={item} onClose={() => removeToast(item.id)} />
      ))}
    </div>
  );
}
